//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import { IContentAggregateMediaStats } from '@apis/content-library/contentLibraryApiModel';
import {
  STAT_BUBBLE_LABELS,
  STAT_BUBBLE_NAMES,
  STAT_BUBBLE_TOOLTIPS,
  StatBubbleLabelKey,
} from '@collections/constants/CollectionsConstants';
import { isDefined, isUndefined } from '@utils/filterPredicates';
import {
  optionalReadableNumber,
  optionalReadableRateWithRawDecimal,
} from '@utils/readableNumber';

interface BubbleStatItem {
  value: string;
  label: string;
  tooltip: string | undefined;
  key: number;
}

const getNumberBubbleStat = (
  key: number,
  value: number,
  name: StatBubbleLabelKey,
): BubbleStatItem => {
  return {
    value: optionalReadableNumber(value),
    label: STAT_BUBBLE_LABELS[name],
    tooltip: STAT_BUBBLE_TOOLTIPS[name],
    key: key,
  };
};

const getPercentageBubbleStat = (
  key: number,
  value: number,
  name: StatBubbleLabelKey,
): BubbleStatItem => {
  return {
    value: optionalReadableRateWithRawDecimal(value),
    label: STAT_BUBBLE_LABELS[name],
    tooltip: STAT_BUBBLE_TOOLTIPS[name],
    key: key,
  };
};

export const processStoryAggregateStats = (value: number, name: string) => {
  switch (name) {
    case STAT_BUBBLE_NAMES.AVG_REACH: {
      return getNumberBubbleStat(0, value, name);
    }
    case STAT_BUBBLE_NAMES.TOTAL_IMPRESSIONS: {
      return getNumberBubbleStat(1, value, name);
    }
    case STAT_BUBBLE_NAMES.AVG_COMPLETION_RATE: {
      return getPercentageBubbleStat(2, value, name);
    }
    default: {
      return undefined;
    }
  }
};

export const processPinterestAggregateStats = (value: number, name: string) => {
  switch (name) {
    case STAT_BUBBLE_NAMES.TOTAL_REPINS: {
      return getNumberBubbleStat(0, value, name);
    }
    case STAT_BUBBLE_NAMES.TOTAL_COMMENTS: {
      return getNumberBubbleStat(1, value, name);
    }
    case STAT_BUBBLE_NAMES.AVG_REPINS: {
      return getNumberBubbleStat(2, value, name);
    }
    case STAT_BUBBLE_NAMES.AVG_COMMENTS: {
      return getNumberBubbleStat(3, value, name);
    }
    default: {
      return undefined;
    }
  }
};

export const processInstagramAggregateStats = (value: number, name: string) => {
  switch (name) {
    case STAT_BUBBLE_NAMES.FOLLOWER_ENGAGEMENT_RATE: {
      return getPercentageBubbleStat(0, value, name);
    }
    case STAT_BUBBLE_NAMES.TOTAL_LIKES: {
      return getNumberBubbleStat(1, value, name);
    }
    case STAT_BUBBLE_NAMES.AVG_LIKES: {
      return getNumberBubbleStat(2, value, name);
    }
    case STAT_BUBBLE_NAMES.TOTAL_COMMENTS: {
      return getNumberBubbleStat(3, value, name);
    }
    case STAT_BUBBLE_NAMES.AVG_COMMENTS: {
      return getNumberBubbleStat(4, value, name);
    }
    default: {
      return undefined;
    }
  }
};

export const processStats = (
  itemStats: IContentAggregateMediaStats | undefined,
): BubbleStatItem[] => {
  if (isUndefined(itemStats)) {
    return [];
  }

  const process = (
    statBubbles: string[],
    processStatsFn: (value: number, name: string) => BubbleStatItem | undefined,
  ): BubbleStatItem[] => {
    return Object.entries(itemStats)
      .filter(([key]) => statBubbles.includes(key))
      .map(([key, value]) => processStatsFn(value, key))
      .filter(isDefined)
      .sort((a, b) => a.key - b.key);
  };

  if (itemStats.storyPercentage === 1.0) {
    return process(
      [
        STAT_BUBBLE_NAMES.AVG_REACH,
        STAT_BUBBLE_NAMES.AVG_COMPLETION_RATE,
        STAT_BUBBLE_NAMES.TOTAL_IMPRESSIONS,
      ],
      processStoryAggregateStats,
    );
  } else if (itemStats.pinPercentage && itemStats.pinPercentage > 0.9) {
    return process(
      [
        STAT_BUBBLE_NAMES.AVG_REPINS,
        STAT_BUBBLE_NAMES.TOTAL_REPINS,
        STAT_BUBBLE_NAMES.AVG_COMMENTS,
        STAT_BUBBLE_NAMES.TOTAL_COMMENTS,
      ],
      processPinterestAggregateStats,
    );
  } else {
    return process(
      [
        STAT_BUBBLE_NAMES.TOTAL_LIKES,
        STAT_BUBBLE_NAMES.AVG_LIKES,
        STAT_BUBBLE_NAMES.TOTAL_COMMENTS,
        STAT_BUBBLE_NAMES.AVG_COMMENTS,
        STAT_BUBBLE_NAMES.FOLLOWER_ENGAGEMENT_RATE,
      ],
      processInstagramAggregateStats,
    );
  }
};
